import { action, makeObservable, observable, runInAction } from 'mobx'

import { UploadingType } from '~/client/graph'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { FileUploadingStore } from '../../stores/domain/FileUploading.store'
import {
  validateFileDimensions,
  validateFileSize,
} from '../../utils/fileValidationHelper'
import { EMPTY_STRING } from '../../utils/usefulStrings'

const TIMEOUT = 3000

export default class AvatarInputStore {
  @observable public errorMessage: string = EMPTY_STRING
  @observable public isAvatarEditModalShown: boolean = false
  @observable public isLoading: boolean = false

  public constructor(
    protected readonly onChange: (newUrl: string) => void,
    private readonly fileUploadingStore: FileUploadingStore,
  ) {
    makeObservable(this)
  }

  private async uploadAvatar(file: File, projectId: string) {
    try {
      runInAction(() => {
        this.isLoading = true
      })
      const [result] = await this.fileUploadingStore.uploadFile(
        file,
        UploadingType.Avatar,
        projectId,
        file.name,
      )
      this.handleSuccess(result.fileURL)
    } catch {
      this.handleError()
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  @action.bound
  public async change(
    file: File,
    projectId: string,
    maxFileSize?: number,
    maxWidth?: number,
    maxHeight?: number,
  ) {
    if (!file) {
      return
    }

    this.clearError()

    if (maxFileSize && !validateFileSize(file, maxFileSize)) {
      this.handleError(
        Localization.translator.fileSizeCanNotBeGreaterThan(
          `${maxFileSize / 1024 / 1024}MB`,
        ),
      )
      return
    }
    if (
      (maxWidth || maxHeight) &&
      !(await validateFileDimensions(file, maxWidth, maxHeight))
    ) {
      this.handleError(
        Localization.translator.imageDimensionsCanNotBeGreaterThan(
          maxWidth,
          maxHeight,
        ),
      )
      return
    }

    this.uploadAvatar(file, projectId)
  }

  @action.bound
  public remove() {
    this.onChange(EMPTY_STRING)
  }

  @action.bound
  public closeAvatarEditModal() {
    this.isAvatarEditModalShown = false
  }

  @action.bound
  protected handleSuccess(url: string) {
    this.onChange(url)
  }

  @action.bound
  protected handleError(errorMessage?: string) {
    this.errorMessage =
      errorMessage ||
      Localization.translator.somethingWentWrongDuringAPIInteraction

    setTimeout(() => {
      this.clearError()
    }, TIMEOUT)
  }

  @action.bound
  protected clearError() {
    this.errorMessage = EMPTY_STRING
  }
}
